import { COMPANY_SHELL_CSV_HEADERS } from "./parseCompanyShellCsv";

/**
 * Outcome of turning a Doc2Company answer into upload CSV. A failure carries a
 * user-facing `reason` rather than throwing, because every failure here is
 * something the user has to act on (retry, or fix the document) and the screen
 * needs to say which.
 */
export type CompanyShellJsonToCsvResult =
  | {
      ok: true;
      csv: string;
      rowCount: number;
      /**
       * Contract columns the agent omitted. Serialised as blank rather than
       * treated as fatal: a blank is indistinguishable from `""` downstream and
       * is repairable in Review, so losing a whole extraction over one absent
       * optional key would be worse than surfacing it. Reported so prompt drift
       * is visible instead of silent.
       */
      missingColumns: string[];
    }
  | { ok: false; reason: string };

/**
 * Pull the JSON array out of a free-text answer.
 *
 * The agent has no structured output type, so the JSON contract is
 * prompt-enforced only and a run may wrap the payload in prose or a code fence.
 * Slicing to the outermost brackets tolerates that without hiding a genuinely
 * malformed payload, which still fails to parse.
 */
function sliceJsonArray(answer: string): string | null {
  const start = answer.indexOf("[");
  const end = answer.lastIndexOf("]");
  if (start === -1 || end === -1 || end < start) return null;
  return answer.slice(start, end + 1);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Coerce one extracted value to its CSV text.
 *
 * Scalars are coerced rather than rejected — a model emitting `23310` instead
 * of `"23310"` is a formatting slip with no ambiguity about intent. Objects and
 * arrays are rejected, because flattening one would have to guess at an order,
 * and guessing at placement is the failure this whole contract exists to avoid.
 */
function coerceValue(value: unknown): string | null {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}

/** RFC 4180: quote when the value contains a delimiter, quote or newline. */
function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Convert a Doc2Company `answer` into the upload CSV the existing
 * `parseCompanyShellCsv` path accepts.
 *
 * The agent is asked for JSON keyed by column name rather than for CSV, so the
 * comma positions are written here instead of by the model. A smoke test
 * against a real questionnaire showed the CSV-emitting prompt shifting a run of
 * ten fields one position right while still producing plausible values — an
 * error neither the client-side requiredness checks nor the server validator
 * can detect. Keying by column name makes a placement error impossible and
 * downgrades a contract breach to something this function can report.
 */
export function companyShellJsonToCsv(
  answer: string,
): CompanyShellJsonToCsvResult {
  const slice = sliceJsonArray(answer);
  if (slice === null) {
    return {
      ok: false,
      reason:
        "The document assistant did not return company data. Try uploading the document again.",
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(slice);
  } catch {
    return {
      ok: false,
      reason:
        "The document assistant returned a response we could not read. Try uploading the document again.",
    };
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return {
      ok: false,
      reason:
        "No companies were found in this document. Check it is a completed payroll implementation questionnaire.",
    };
  }

  if (!parsed.every(isPlainObject)) {
    return {
      ok: false,
      reason:
        "The document assistant returned company data in an unexpected shape. Try uploading the document again.",
    };
  }

  const rows = parsed as Record<string, unknown>[];
  // The prompt asks for lowercase keys; the CSV header row has to carry the
  // template's own casing, which Review's field lookup is keyed by. Matching
  // case-insensitively lets the prompt stay readable and absorbs a run that
  // capitalises a key.
  const headerForKey = new Map(
    COMPANY_SHELL_CSV_HEADERS.map((header) => [header.toLowerCase(), header]),
  );
  const missingColumns = new Set<string>();
  const unknownColumns = new Set<string>();
  const lines: string[] = [];

  for (const row of rows) {
    const valueForHeader = new Map<string, unknown>();
    for (const key of Object.keys(row)) {
      const header = headerForKey.get(key.trim().toLowerCase());
      if (header === undefined) {
        unknownColumns.add(key);
        continue;
      }
      valueForHeader.set(header, row[key]);
    }

    const fields: string[] = [];
    for (const header of COMPANY_SHELL_CSV_HEADERS) {
      if (!valueForHeader.has(header)) {
        missingColumns.add(header);
        fields.push("");
        continue;
      }
      const value = coerceValue(valueForHeader.get(header));
      if (value === null) {
        return {
          ok: false,
          reason: `The document assistant returned an unexpected value for "${header}". Try uploading the document again.`,
        };
      }
      fields.push(csvField(value));
    }
    lines.push(fields.join(","));
  }

  // Fatal, unlike a missing column: a value under a column this upload does not
  // have cannot be placed, and dropping it silently would discard extracted
  // data without telling anyone.
  if (unknownColumns.size > 0) {
    return {
      ok: false,
      reason: `The document assistant returned columns this upload does not accept: ${[
        ...unknownColumns,
      ].join(", ")}.`,
    };
  }

  const header = COMPANY_SHELL_CSV_HEADERS.map(csvField).join(",");
  return {
    ok: true,
    csv: [header, ...lines].join("\n"),
    rowCount: rows.length,
    missingColumns: [...missingColumns],
  };
}
